// Helpers for validating and normalizing user-entered social/profile links.
//
// Users often paste links without a scheme (e.g. "instagram.com/me" or just
// "myhandle"). A schemeless href is treated by the browser as a *relative*
// path, which navigates inside the app (e.g. /myhandle) and lands on a blank
// recipe/collection page. We therefore:
//  - normalize: prepend https:// when no scheme is present
//  - validate: require the normalized value to parse as an http(s) URL with
//    a real hostname containing a dot (e.g. "instagram.com"), and for known
//    platforms, require the hostname to belong to that platform.

// Allowed domains per platform key. A hostname matches if it equals the
// domain or is a subdomain of it (e.g. www.youtube.com, britt.substack.com).
// "website" is intentionally absent: any valid URL is allowed there.
export const PLATFORM_DOMAINS = {
  instagram: ['instagram.com'],
  tiktok: ['tiktok.com'],
  youtube: ['youtube.com', 'youtu.be'],
  facebook: ['facebook.com', 'fb.com'],
  x: ['x.com', 'twitter.com'],
  threads: ['threads.net', 'threads.com'],
  substack: ['substack.com'],
};

// Example placeholder shown in each platform's input.
export const PLATFORM_PLACEHOLDERS = {
  instagram: 'instagram.com/yourhandle',
  tiktok: 'tiktok.com/@yourhandle',
  youtube: 'youtube.com/@yourhandle',
  facebook: 'facebook.com/yourname',
  x: 'x.com/yourhandle',
  threads: 'threads.com/@yourhandle',
  substack: 'yourname.substack.com',
  website: 'yoursite.com',
};

export function normalizeSocialUrl(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function hostMatches(hostname, domain) {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

export function isValidSocialUrl(value, platform) {
  const normalized = normalizeSocialUrl(value);
  if (!normalized) return false;
  // Reject embedded whitespace (new URL() would silently percent-encode it).
  if (/\s/.test(normalized)) return false;
  let url;
  try {
    url = new URL(normalized);
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  const host = url.hostname.toLowerCase();
  // Require a dot in the hostname so bare words like "myhandle" fail.
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) return false;
  const domains = PLATFORM_DOMAINS[platform];
  if (domains) {
    return domains.some((d) => hostMatches(host, d));
  }
  return true;
}
